import { useState } from "react";

import { useEditorState } from "@tiptap/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";
import { useResizeObserver } from "@/utils/useResizeObserver";

import { getChangeRequestPendingAt } from "./ChangeRequest/helpers";
import { useChangeRequestPending } from "./ChangeRequest/useChangeRequestPending";

import type { Editor } from "@tiptap/react";

const toolbarButtonClass =
  "select-none rounded-full px-4 py-2 text-[15px] leading-[18px] font-normal transition-colors";

const mutedToolbarButtonClass =
  "cursor-not-allowed bg-gray-100/80 text-gray-500 opacity-80 dark:bg-gray-900/80 dark:text-gray-400";

const ChangeRequestToolbarView = observer(function ChangeRequestToolbarView({
  editor,
  count,
  pendingAt,
}: {
  editor: Editor;
  count: number;
  pendingAt: number | undefined;
}) {
  const t = useTranslations("doc.changeRequest.toolbar");
  const { chats } = useStore();
  const pending = useChangeRequestPending(pendingAt);
  const disabled = pending || (chats?.isBotGeneratingForCurrentDoc ?? false);

  const element = editor.options.element instanceof HTMLElement ? editor.options.element : null;
  const [centerX, setCenterX] = useState<number | null>(null);

  useResizeObserver(element, (entry) => {
    const rect = entry.target.getBoundingClientRect();
    setCenterX(rect.left + rect.width / 2);
  });

  const approveAll = () => {
    if (!disabled) {
      editor.chain().approveAllChangeRequests().run();
    }
  };
  const rejectAll = () => {
    if (!disabled) {
      editor.chain().rejectAllChangeRequests().run();
    }
  };

  return (
    <div
      style={{ left: centerX ?? "50%" }}
      className={twMerge(
        "fixed bottom-20 z-20 -translate-x-1/2",
        !chats?.isFloatingInputShown && "fromSm:bottom-5",
        "flex w-max max-w-[calc(100vw-2rem)] flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-14 p-2",
        "bg-white ring-1 ring-gray-200",
        "dark:bg-gray-900 dark:ring-gray-800",
      )}
    >
      <span className="flex items-center text-[15px] text-gray-500 dark:text-gray-400">
        <Icon name="AiHelper" className="mr-1.5" />
        {t("updateCount", { count })}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={rejectAll}
          disabled={disabled}
          className={twMerge(
            toolbarButtonClass,
            disabled
              ? mutedToolbarButtonClass
              : "bg-red-100/90 text-gray-800 hover:bg-red-200 active:bg-red-300 dark:bg-red-950 dark:text-red-100 dark:hover:bg-red-900",
          )}
        >
          {t("rejectAll")}
        </button>
        <button
          type="button"
          onClick={approveAll}
          disabled={disabled}
          className={twMerge(
            toolbarButtonClass,
            disabled
              ? mutedToolbarButtonClass
              : "bg-blue-100/90 text-gray-900 hover:bg-blue-200 active:bg-blue-300 dark:bg-blue-950 dark:text-blue-100 dark:hover:bg-blue-900",
          )}
        >
          {t("approveAll")}
        </button>
      </div>
    </div>
  );
});

export function ChangeRequestToolbar({ editor }: { editor: Editor | null }) {
  const { count, pendingAt } = useEditorState({
    editor,
    selector: () => {
      let n = 0;
      let latestPendingAt: number | undefined;
      editor?.state.doc.descendants((node) => {
        if (node.type.name === "changeRequest") {
          n += 1;
          const nodePendingAt = getChangeRequestPendingAt(node);
          if (nodePendingAt !== undefined) {
            latestPendingAt = Math.max(latestPendingAt ?? 0, nodePendingAt);
          }
          return false;
        }
        return true;
      });
      return { count: n, pendingAt: latestPendingAt };
    },
  }) ?? { count: 0, pendingAt: undefined };

  if (!editor || !count) return null;

  return <ChangeRequestToolbarView editor={editor} count={count} pendingAt={pendingAt} />;
}
